import { Box, Card, CardContent, Grid2, Typography } from '@mui/material';
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import InputFecha from '../../../components/inputs/yup/materialui/inputFecha';
import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import React, { useEffect } from 'react';
import InputSwitch from '../../../components/inputs/yup/materialui/inputSwitch';
import TablaGenerarDiv from './tablaGenerarDiv';
import type { DividendoValores } from '../models/dividendos';
import MButton from '../../../components/inputs/yup/materialui/mButton';
import { Close, SaveAlt } from '@mui/icons-material';
import { useGenerarDiv } from '../hooks/useGenerarDiv';
import { postGuardarDivIndividual } from '../services/servicesDividendos';
import { StatusCode } from '../../../utils/enums';
import { useToast } from '../../../hooks/useToast';
import Conversiones from '../../../utils/conversiones';
import { useRecaudacionIndividual } from '../hooks/useRecaudacionIndividual';

interface IProps {
    idMutuo: number;
    handleCancelar: () => void;
}

const schema = Yup.object({
    seguroIncendioMO: Yup.number().required(),
    seguroDesgravamenMO: Yup.number().required(),
    seguroCesantiaMO: Yup.number().required(),
    interesMoraMO: Yup.number().required(),
    gastosCobranzaMO: Yup.number().required(),
    interesMoraPesos: Yup.number().required(),
    gastosCobranzaPesos: Yup.number().required(),
    otrosCargosMO: Yup.number().required(),
    otrosAbonosMO: Yup.number().required(),
    otrosCargosPesos: Yup.number().required(),
    otrosAbonosPesos: Yup.number().required(),
    interesMO: Yup.number().required(),
    amortizacionMO: Yup.number().required(),
    totalPagarPesos: Yup.number().required(),
    totalRecibidoPesos: Yup.number().required(),
    paridadMonedaOrigenPesos: Yup.number().required(),
    fechaPago: Yup.string()
        .nullable()
        .required('La fecha es obligatoria')
        .test(
            'fecha-no-menor-hoy',
            'La fecha no puede ser menor que hoy',
            value => {
                if (!value) return false;

                // Parse date as local date to avoid timezone issues
                const [year, month, day] = value.split('T')[0].split('-').map(Number);
                const inputDate = new Date(year, month - 1, day);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                console.log('inputDate', inputDate)
                console.log('today', today)
                return inputDate >= today;
            }
        ),
    condonarCargos: Yup.boolean().required(),
    condonarGastos: Yup.boolean().required(),
    condonarOtrosCargos: Yup.boolean().required(),
    idSeguroCesantia: Yup.number().required(),
    subsidioCubierto: Yup.number().required(),
    subsidioNoCubierto: Yup.number().required(),
    mesesSubsidio: Yup.number().required(),

}) as Yup.ObjectSchema<DividendoValores>;

const GenerarDividendo: React.FC<IProps> = ({ idMutuo, handleCancelar }) => {
    const { defaultValues, cuota, calcular, setDefaultValues, obtenerDividendos } = useGenerarDiv();
    const { addToast } = useToast();

    const {
        formState: { errors, isDirty },
        control,
        reset,
        watch,
        handleSubmit,
    } = useForm<DividendoValores>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema),
        mode: 'onChange',
    })
    const { estado, fechaPago, condonarCargos, condonarGastos, condonarOtrosCargos, numeroOperacion } = watch();

    useEffect(() => {
        obtenerDividendos(idMutuo);
    }, [idMutuo])

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues])

    const onSubmit = async (data: DividendoValores) => {
        const payload: DividendoValores = {
            ...data,
            otrosSegurosMOParaCobroFuturo: defaultValues.otrosSegurosMOParaCobroFuturo,
            seguroIncendioMOParaCobroFuturo: defaultValues.seguroIncendioMOParaCobroFuturo,
            seguroDesgravamenMOParaCobroFuturo: defaultValues.seguroDesgravamenMOParaCobroFuturo,
            seguroCesantiaMOParaCobroFuturo: defaultValues.seguroCesantiaMOParaCobroFuturo,
        };
        const resp = await postGuardarDivIndividual(payload);
        if (resp.status === StatusCode.NoContent) {
            addToast({
                mostrar: true,
                titulo: 'Éxito',
                mensaje: 'Dividendo generado correctamente',
                tipo: 'Success'
            });
            
            handleCancelar();
        }
    }

    const isFirstRender = React.useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!fechaPago && numeroOperacion !== "")
            return;

        handleCalculo(fechaPago);

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condonarCargos, condonarGastos, condonarOtrosCargos]);

    const handleCalculo = async (fechaPago: string | null) => {
        if (!fechaPago && !numeroOperacion)
            return;

        const currentValues = watch();
        const nuevosValores = {
            ...currentValues,
            fechaPago: fechaPago,
            condonarCargos: condonarCargos ?? false,
            condonarGastos: condonarGastos ?? false,
            condonarOtrosCargos: condonarOtrosCargos ?? false,
        };

        await calcular(nuevosValores);
    }

    return (
        <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
            <CardContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid2 container alignItems="center" spacing={2}>
                        <Grid2 size={2}>
                            <Typography className='card-title' variant="h6">Generar dividendo</Typography>
                        </Grid2>

                        <Grid2 container spacing={1} size={10} sx={{ textAlign: "right", justifyContent: 'flex-end' }}>

                            <MButton
                                variant='contained'
                                color='primary'
                                // disabled={!(isDirty && isValid) || isSubmitting}
                                startIcon={<SaveAlt />}
                                onClick={handleSubmit(onSubmit)}
                            >
                                Generar
                            </MButton>

                            <MButton
                                variant='contained'
                                color={'inherit'}
                                startIcon={<Close />}
                                onClick={() => { handleCancelar(); }}
                            >
                                Cancelar
                            </MButton>

                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} mt={2}>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputText
                                label="Emisor"
                                name="emisor"
                                readonly
                                control={control}
                                error={errors.emisor}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputText
                                label="Código mutuo"
                                name="codigoMutuo"
                                readonly
                                control={control}
                                error={errors.codigoMutuo}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputText
                                label="OMD"
                                name="omd"
                                readonly
                                control={control}
                                error={errors.omd}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputSwitch
                                label="Administrado"
                                name="esAdministrado"
                                control={control}
                                error={errors.esAdministrado}
                                disabled
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputText
                                label="N° operación"
                                name="numeroOperacion"
                                readonly
                                control={control}
                                error={errors.numeroOperacion}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputText
                                label="Nombre"
                                name="nombre"
                                readonly
                                control={control}
                                error={errors.nombre}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="N° dividendo"
                                name="numeroDividendo"
                                readonly
                                control={control}
                                error={errors.numeroDividendo}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 6 }}>
                            <InputText
                                label="Dirección"
                                name="direccion"
                                readonly
                                control={control}
                                error={errors.direccion}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputText
                                label="N° dividendos cancelados"
                                name="numeroDividendosCancelados"
                                readonly
                                control={control}
                                error={errors.numeroDividendosCancelados}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputText
                                label="Estado"
                                name="estado"
                                readonly
                                control={control}
                                error={errors.estado}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Deuda original (MO)"
                                name="deudaOriginal"
                                readonly
                                control={control}
                                esMoneda
                                error={errors.deudaOriginal}
                                decimales={4}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Saldo actual (MO)"
                                name="saldoActual"
                                readonly
                                control={control}
                                esMoneda
                                error={errors.saldoActual}
                                decimales={4}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Amortización (MO)"
                                name="amortizacionMO"
                                readonly
                                control={control}
                                esMoneda
                                error={errors.amortizacionMO}
                                decimales={4}
                            // prefix='UF'
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Interés (MO)"
                                name="interesMO"
                                readonly
                                control={control}
                                esMoneda
                                error={errors.interesMO}
                                decimales={4}
                            // prefix='CLP'
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Seguro incendio (MO)"
                                name="seguroIncendioMO"
                                readonly
                                control={control}
                                esMoneda
                                error={errors.seguroIncendioMO}
                                decimales={4}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Seguro desgravamen (MO)"
                                name="seguroDesgravamenMO"
                                readonly
                                control={control}
                                esMoneda
                                decimales={4}
                                error={errors.seguroDesgravamenMO}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Seguro cesantía (MO)"
                                name="seguroCesantiaMO"
                                readonly
                                control={control}
                                esMoneda
                                error={errors.seguroCesantiaMO}
                                decimales={4}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Otros seguros (MO)"
                                name="otrosSegurosMO"
                                readonly
                                control={control}
                                esMoneda
                                error={errors.otrosSegurosMO}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Interés mora"
                                name="interesMoraMO"
                                readonly
                                control={control}
                                suffix='CLP'
                                decimales={4}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Gastos cobranza"
                                name="gastosCobranzaMO"
                                readonly
                                suffix='CLP'
                                decimales={4}
                                control={control}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Otros cargos"
                                name="otrosCargosMO"
                                suffix='CLP'
                                decimales={4}
                                readonly
                                control={control}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Otros abonos"
                                name="otrosAbonosMO"
                                suffix='CLP'
                                decimales={4}
                                readonly
                                control={control}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputFecha
                                label="Fecha pago"
                                name="fechaPago"
                                control={control}
                                onChange={(value) => { handleCalculo(value?.toISOString() ?? '') }}
                                rangoFechaInicio={new Date().toISOString().split('T')[0]} // Fecha actual
                                fecha={Conversiones.parseLocalDate(fechaPago)}
                                error={errors.fechaPago}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputNumber
                                label="Paridad moneda origen"
                                name="paridadMonedaOrigenPesos"
                                prefix='CLP'
                                readonly
                                control={control}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputFecha
                                label="Fecha vencimiento"
                                name="fechaVencimiento"
                                esReadOnly={true}
                                control={control}
                                fecha={defaultValues.fechaVencimiento}
                            />
                        </Grid2>
                        {/* <Grid2 size={{ xs: 12, lg: 3 }}>
                            <InputFecha
                                label="Fecha de activación"
                                name="fechaActivacion"
                                esReadOnly={true}
                                control={control}
                                fecha={defaultValues.fechaActivacion}
                            />
                        </Grid2> */}
                    </Grid2>
                    <Grid2 container spacing={2} mt={2}>
                        {estado === 'Atrasado' &&
                            <>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSwitch
                                        label="¿Condonar cargos por mora?"
                                        name="condonarCargos"
                                        control={control}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSwitch
                                        label="¿Condonar gastos de cobranza?"
                                        name="condonarGastos"
                                        control={control}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSwitch
                                        label="¿Condonar otros cargos?"
                                        name="condonarOtrosCargos"
                                        control={control}
                                    />
                                </Grid2>
                            </>
                        }
                        <Grid2 size={{ xs: 12, lg: 6 }}>
                            <InputNumber
                                label="Total a pagar"
                                name="totalPagarPesos"
                                prefix='CLP'
                                readonly
                                control={control}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, lg: 6 }}>
                            <InputNumber
                                label="Total recibido"
                                name="totalRecibidoPesos"
                                prefix='CLP'
                                readonly
                                control={control}
                            />
                        </Grid2>
                    </Grid2>
                </form>
                {cuota.length > 0 && (
                    <Box mt={5}>
                        <TablaGenerarDiv
                            data={cuota} />
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}

export default GenerarDividendo;